import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';

/**
 * Pencil-reveal designer picker, shared by the creatives table (desktop `<td>`
 * and mobile card) and by `DetailDrawer`'s Info tab.
 *
 * Deliberately self-contained: Tailwind utilities only (no `.scss`) and a
 * native `<select>` rather than react-select, because the drawer host is a
 * fixed `z-50` panel and a portalled menu fights with it. Busy state stays
 * local — the parent owns the API call, the toast and the row patch, and only
 * tells us success/failure through `onAssign`'s resolved value.
 */
const DesignerAssignControl = ({ row, designers, locked, onAssign }) => {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The control is reused across rows (table virtualisation, drawer swaps) —
  // collapse the editor whenever the creative underneath it changes so a half
  // finished pick can never leak onto a different row.
  useEffect(() => {
    setEditing(false);
    setSelected('');
  }, [row?.bsc_id]);

  const openEditor = useCallback(() => {
    setSelected(row?.designer_id ? String(row.designer_id) : '');
    setEditing(true);
  }, [row]);

  const cancelEditor = useCallback(() => {
    setEditing(false);
    setSelected('');
  }, []);

  const submit = useCallback(async () => {
    if (submitting || !selected) return;
    const picked = (designers || []).find((d) => String(d?.designer_id) === String(selected));
    if (!picked) return;
    setSubmitting(true);
    try {
      const ok = await onAssign?.(row, picked.designer_id, picked.name);
      if (!mountedRef.current) return;
      // Only a strict `true` closes the reveal — on a failure we keep the
      // picker open with the selection intact so the user can just retry.
      if (ok === true) {
        setEditing(false);
        setSelected('');
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [submitting, selected, designers, onAssign, row]);

  if (locked) {
    return row?.designer_name ? (
      <span className="block max-w-[10rem] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-700">
        {row.designer_name}
      </span>
    ) : (
      <span className="text-xs italic text-slate-400">—</span>
    );
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        {row?.designer_name ? (
          <span className="max-w-[10rem] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-700">
            {row.designer_name}
          </span>
        ) : (
          <span className="whitespace-nowrap text-xs italic text-slate-400">Unassigned</span>
        )}
        <sl-tooltip content={row?.designer_name ? 'Change designer' : 'Assign designer'}>
          <sl-icon-button library="lucide" name="edit" onClick={openEditor}></sl-icon-button>
        </sl-tooltip>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <select
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 disabled:opacity-60"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={submitting}
      >
        <option value="">Select designer</option>
        {(designers || []).map((d) => (
          <option key={d.designer_id} value={String(d.designer_id)}>
            {d.name}
          </option>
        ))}
      </select>

      <button
        type="button"
        aria-label="Save designer"
        onClick={submit}
        disabled={submitting || !selected}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? (
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        ) : (
          <SlIcon library="lucide" name="check" style={{ fontSize: '13px' }} />
        )}
      </button>

      <button
        type="button"
        aria-label="Cancel"
        onClick={cancelEditor}
        disabled={submitting}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <SlIcon library="lucide" name="x" style={{ fontSize: '13px' }} />
      </button>
    </div>
  );
};

export default DesignerAssignControl;
